/**
 * Sprite-key registry for AI-generated tank PNGs.
 *
 * Maps faction IDs → player tank sprite asset key, and enemy variant types →
 * enemy tank sprite asset key. Pure (no Phaser dep) so it can be unit-tested
 * without DOM.
 *
 * The actual PNG files live in `public/assets/sprites/tank-{role}-{kind}.png`
 * and are preloaded by HorizontalBattleScene. When the asset is missing from
 * the Phaser textures cache (e.g. file not present on disk in a stripped
 * deployment), the rendering code falls back to the existing Phaser.Graphics
 * shapes — see HorizontalBattleScene._drawPlayer / _spawnEnemy.
 */

import type { FactionId } from '../../shared/types';

// ── Player tank sprite keys (5 factions) ──────────────────────────────────────

export const PLAYER_TANK_SPRITE_KEYS: Record<FactionId, string> = {
  celestial: 'tank-player-celestial',
  runic:     'tank-player-runic',
  techno:    'tank-player-techno',
  elemental: 'tank-player-elemental',
  dominion:  'tank-player-dominion',
};

/** Map faction → relative URL of the generated player tank PNG. */
export const PLAYER_TANK_SPRITE_URLS: Record<FactionId, string> = {
  celestial: 'assets/sprites/tank-player-celestial.png',
  runic:     'assets/sprites/tank-player-runic.png',
  techno:    'assets/sprites/tank-player-techno.png',
  elemental: 'assets/sprites/tank-player-elemental.png',
  dominion:  'assets/sprites/tank-player-dominion.png',
};

/**
 * Returns the player tank sprite asset key for a given faction. Always
 * returns a non-empty string — defaults to celestial when given an unknown
 * faction so callers can blindly look up textures.
 */
export function playerTankKeyFor(faction: FactionId | string | undefined | null): string {
  if (!faction) return PLAYER_TANK_SPRITE_KEYS.celestial;
  return (PLAYER_TANK_SPRITE_KEYS as Record<string, string>)[faction]
    ?? PLAYER_TANK_SPRITE_KEYS.celestial;
}

// ── Hero character portrait keys (5 factions) ─────────────────────────────────
//
// hero-portrait-wire (boss 2026-06-15「主角放進車庫」): AI-gen 全身英雄立繪
// (劍星風 SB key-art,含特效背景) per 門派, shown as a framed hero card on the
// GarageScene central stage. Files live in `public/assets/ai-gen/heroes/hero-<faction>.png`.
// 這批是「先用現有圖佔位」(Cloudflare 免費生圖額度當天用罄),正式全套(正常+戰損、
// 5 女 5 男差異化)等額度重置後由 tools/gen-hero-keyart.mjs 全鋪、同檔名覆蓋。
// texture-exists gate:缺檔 → GarageScene 回退舊的坦克展示台。

export const HERO_PORTRAIT_KEYS: Record<FactionId, string> = {
  celestial: 'hero-portrait-celestial',
  runic:     'hero-portrait-runic',
  techno:    'hero-portrait-techno',
  elemental: 'hero-portrait-elemental',
  dominion:  'hero-portrait-dominion',
};

/** Map faction → relative URL of the hero portrait PNG. */
export const HERO_PORTRAIT_URLS: Record<FactionId, string> = {
  celestial: 'assets/ai-gen/heroes/hero-celestial.png',
  runic:     'assets/ai-gen/heroes/hero-runic.png',
  techno:    'assets/ai-gen/heroes/hero-techno.png',
  elemental: 'assets/ai-gen/heroes/hero-elemental.png',
  dominion:  'assets/ai-gen/heroes/hero-dominion.png',
};

/**
 * Returns the hero portrait asset key for a faction. Always non-empty —
 * defaults to celestial for unknown/missing factions so the texture-exists gate
 * has a key to look up (and falls back to the tank display when absent).
 */
export function heroPortraitKeyFor(faction: FactionId | string | undefined | null): string {
  if (!faction) return HERO_PORTRAIT_KEYS.celestial;
  return (HERO_PORTRAIT_KEYS as Record<string, string>)[faction]
    ?? HERO_PORTRAIT_KEYS.celestial;
}

// ── Hero 半身卡 busts (boss 2026-06-20「卡牌用半身,大背景才用全身」) ────────────
// gacha 慣例:左側資訊卡用「半身像」(頭+肩+上身),中央大舞台才用全身 splash。
// 半身檔 `assets/ai-gen/heroes/busts/hero-bust-<faction>.png`(已重訓門派用乾淨半身,
// 其餘從全身裁上半身)。缺檔 → GarageScene 半身卡回退不顯示(不影響全身 splash)。
export const HERO_BUST_KEYS: Record<FactionId, string> = {
  celestial: 'hero-bust-celestial',
  runic:     'hero-bust-runic',
  techno:    'hero-bust-techno',
  elemental: 'hero-bust-elemental',
  dominion:  'hero-bust-dominion',
};
export const HERO_BUST_URLS: Record<FactionId, string> = {
  celestial: 'assets/ai-gen/heroes/busts/hero-bust-celestial.png',
  runic:     'assets/ai-gen/heroes/busts/hero-bust-runic.png',
  techno:    'assets/ai-gen/heroes/busts/hero-bust-techno.png',
  elemental: 'assets/ai-gen/heroes/busts/hero-bust-elemental.png',
  dominion:  'assets/ai-gen/heroes/busts/hero-bust-dominion.png',
};

// ── Per-gender hero portraits (boss 2026-06-16「五個顏色都要有男女生」) ──────────
//
// hero-gender-wire: each 門派 now has BOTH a female and a male hero portrait
// (`hero-<faction>-<f|m>.png`), generated locally (WAI/Prefect, see
// project_local_comfyui_pipeline). The GarageScene shows one at a time with a
// ♀/♂ toggle. 純展示 / 0 戰力寫入 — which gender is shown never touches
// factionXP / skillLevels / 傷害 (CLAUDE.md 鐵則). Note 自然門 = 'elemental'.
// texture-exists gated: missing gendered file → falls back to the no-gender
// `hero-<faction>.png` → tank display (see GarageScene._makeHeroDisplay).

export type HeroGender = 'f' | 'm';
export const HERO_GENDERS: readonly HeroGender[] = ['f', 'm'] as const;

/** `<faction>-<gender>` → Phaser texture asset key (10 entries). */
export const HERO_PORTRAIT_GENDER_KEYS: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const faction of Object.keys(HERO_PORTRAIT_KEYS) as FactionId[]) {
    for (const g of HERO_GENDERS) {
      out[`${faction}-${g}`] = `hero-portrait-${faction}-${g}`;
    }
  }
  return out;
})();

/** texture key → relative PNG URL for each gendered hero portrait. */
export const HERO_PORTRAIT_GENDER_URLS: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const faction of Object.keys(HERO_PORTRAIT_KEYS) as FactionId[]) {
    for (const g of HERO_GENDERS) {
      out[`hero-portrait-${faction}-${g}`] = `assets/ai-gen/heroes/hero-${faction}-${g}.png`;
    }
  }
  return out;
})();

/**
 * Returns the gendered hero portrait asset key for (faction, gender). Always a
 * registered key — unknown faction defaults to celestial, unknown gender to 'f'
 * — so the texture-exists gate in GarageScene always has a key to probe (and
 * falls back to the no-gender portrait / tank when the file is absent). Pure.
 */
export function heroPortraitGenderKeyFor(
  faction: FactionId | string | undefined | null,
  gender: HeroGender | string | undefined | null,
): string {
  const f = faction && (HERO_PORTRAIT_KEYS as Record<string, string>)[faction] ? faction : 'celestial';
  const g: HeroGender = gender === 'm' ? 'm' : 'f';
  return HERO_PORTRAIT_GENDER_KEYS[`${f}-${g}`];
}

// ── Hero skill cut-in art (boss 2026-06-16「放技能叫出主角立牌 + 大招圖例外畫」) ──
//
// hero-skill-cutin: per-門派 DYNAMIC skill cut-in artwork (transparent cutout,
// dynamic action pose, NO baked background — VFX is layered by the engine), shown
// sliding in on slot-0 skill cast. Distinct from the calm GarageScene portrait.
// 純演出 / 0 戰力寫入. texture-exists gated: missing cut-in → falls back to the
// hero portrait → tank (HorizontalBattleScene._playHeroCutin). Files live in
// `public/assets/ai-gen/heroes/cutins/hero-cutin-<faction>.png`.

export const HERO_CUTIN_KEYS: Record<FactionId, string> = {
  celestial: 'hero-cutin-celestial',
  runic:     'hero-cutin-runic',
  techno:    'hero-cutin-techno',
  elemental: 'hero-cutin-elemental',
  dominion:  'hero-cutin-dominion',
};

export const HERO_CUTIN_URLS: Record<FactionId, string> = {
  celestial: 'assets/ai-gen/heroes/cutins/hero-cutin-celestial.png',
  runic:     'assets/ai-gen/heroes/cutins/hero-cutin-runic.png',
  techno:    'assets/ai-gen/heroes/cutins/hero-cutin-techno.png',
  elemental: 'assets/ai-gen/heroes/cutins/hero-cutin-elemental.png',
  dominion:  'assets/ai-gen/heroes/cutins/hero-cutin-dominion.png',
};

// 2-frame cut-in (boss 2026-06-16「站立念咒 → 發招時改成發力 pose」): OPTIONAL second
// 「發力 / impact」frame per 門派. Only factions present here play a 2-frame sequence
// (念咒立繪滑入 → 技能觸發 → 換成發力 pose);others stay single-frame. 純演出.
export const HERO_CUTIN2_KEYS: Record<string, string> = {
  celestial: 'hero-cutin-celestial-2',
  runic:     'hero-cutin-runic-2',
  techno:    'hero-cutin-techno-2',
  elemental: 'hero-cutin-elemental-2',
  dominion:  'hero-cutin-dominion-2',
};
export const HERO_CUTIN2_URLS: Record<string, string> = {
  celestial: 'assets/ai-gen/heroes/cutins/hero-cutin-celestial-2.png',
  runic:     'assets/ai-gen/heroes/cutins/hero-cutin-runic-2.png',
  techno:    'assets/ai-gen/heroes/cutins/hero-cutin-techno-2.png',
  elemental: 'assets/ai-gen/heroes/cutins/hero-cutin-elemental-2.png',
  dominion:  'assets/ai-gen/heroes/cutins/hero-cutin-dominion-2.png',
};

// ── Enemy tank sprite keys (3 variants) ───────────────────────────────────────

/** Enemy tank variants used by horizontal-battle-scene wave spawner. */
export type EnemyTankType = 'basic' | 'fast' | 'tank';

export const ENEMY_TANK_SPRITE_KEYS: Record<EnemyTankType, string> = {
  basic: 'tank-enemy-basic',
  fast:  'tank-enemy-fast',
  tank:  'tank-enemy-tank',
};

// ansimuz-sprite-skin (2026-06-05, 老闆「角色畫風=套件A ansimuz sci-fi pixel CC0」):
// 小兵改用 ansimuz「Sideview Sci-Fi」CC0 像素素材的三種不同生物/機甲,取代之前
// 「借用魔王 PNG 當小兵」的權宜做法。實作方式:把 ansimuz 剪影「就地覆寫」進這三個
// tank-enemy-*.png 來源檔(由 scripts/prep-ansimuz-sprites.mjs 產出),並重建 tanks
// atlas — 所以 URL / asset-key / atlas frame name 全部不變,只有圖片內容換成 ansimuz。
// 變種對應:basic=bipedal-unit、fast=alien-flying、tank=alien-walking。
// 方向契約 (2026-06-05, Playwright 逐型實證 docs/qa/fix/cmp-*.png):三型 baked
// atlas frame 全部「朝左」繪製;玩家在左,故 scene 端對三型都 setFlipX(false)
// 讓敵人面向左邊的玩家。(舊註解「朝右、flip(true)」為誤判,已更正。)CC0。
export const ENEMY_TANK_SPRITE_URLS: Record<EnemyTankType, string> = {
  basic: 'assets/sprites/tank-enemy-basic.png',
  fast:  'assets/sprites/tank-enemy-fast.png',
  tank:  'assets/sprites/tank-enemy-tank.png',
};

/**
 * Returns the enemy tank sprite asset key for a given variant. Defaults to
 * 'basic' for unknown values.
 */
export function enemyTankKeyFor(variant: EnemyTankType | string | undefined | null): string {
  if (!variant) return ENEMY_TANK_SPRITE_KEYS.basic;
  return (ENEMY_TANK_SPRITE_KEYS as Record<string, string>)[variant]
    ?? ENEMY_TANK_SPRITE_KEYS.basic;
}

// ── Per-faction enemy tank sprites (new cartoon roster, 2026-06-13) ────────────
//
// roster-wire (boss「戰車怎麼沒套新的」): the AI-gen cartoon tank roster
// (`_tank-roster/`, `docs/spec/TANK_ART_SPEC.md`) is now wired in as the ENEMY +
// BOSS art (the player keeps the existing 5-faction tanks, untouched). Each
// stage has a faction (the hub/level faction), so enemies in a celestial level
// look like celestial war-machines, etc. Two-role factions reuse their roles
// across the three (basic/fast/tank) gameplay variants — the roster only has
// 2–3 distinct chassis per faction.
//
// Direction contract (TANK_ART_SPEC §1, visually verified 2026-06-13 by
// rendering the roster PNGs): every enemy/boss chassis is drawn FACING LEFT
// natively (barrel + face point left). Player is on the LEFT, so enemies face
// the player with NO flipX (`setFlipX(false)`), same as the old ansimuz frames.
//
// File naming: `tank-enemy-<factionId>-<role>.png` (TANK_ART_SPEC §5). Note the
// faction segment uses the code FactionId — 自然門 = 'elemental' (the roster
// source files are named 'nature-*' but the in-game faction key is 'elemental').

/** Faction → variant → roster role used for the enemy sprite. */
const ENEMY_FACTION_ROLE: Record<FactionId, Record<EnemyTankType, string>> = {
  celestial: { basic: 'scout',   fast: 'scout', tank: 'heavy'    },
  runic:     { basic: 'mage',    fast: 'mage',  tank: 'heavy'    },
  techno:    { basic: 'gatling', fast: 'drill', tank: 'gatling'  },
  elemental: { basic: 'vine',    fast: 'dino',  tank: 'dino'     },
  dominion:  { basic: 'mortar',  fast: 'mortar', tank: 'fortress' },
};

/** All distinct (faction, role) enemy sprite keys present on disk. */
export const ENEMY_FACTION_TANK_SPRITE_KEYS: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const faction of Object.keys(ENEMY_FACTION_ROLE) as FactionId[]) {
    for (const role of Object.values(ENEMY_FACTION_ROLE[faction])) {
      const key = `tank-enemy-${faction}-${role}`;
      out[key] = key;
    }
  }
  return out;
})();

/** key → relative PNG URL for each per-faction enemy sprite. */
export const ENEMY_FACTION_TANK_SPRITE_URLS: Record<string, string> = Object.fromEntries(
  Object.keys(ENEMY_FACTION_TANK_SPRITE_KEYS).map((key) => [key, `assets/sprites/${key}.png`]),
);

/**
 * Returns the per-faction enemy tank sprite asset key for a (faction, variant)
 * pair. Falls back to the generic `enemyTankKeyFor(variant)` (basic/fast/tank
 * ansimuz frame) when the faction has no roster art registered — so a stripped
 * deployment or an unknown faction still renders an enemy. Pure / Phaser-free.
 */
export function enemyTankKeyForFaction(
  faction: FactionId | string | undefined | null,
  variant: EnemyTankType | string | undefined | null,
): string {
  const v: EnemyTankType =
    variant === 'fast' || variant === 'tank' ? variant : 'basic';
  const roleMap = faction
    ? ENEMY_FACTION_ROLE[faction as FactionId]
    : undefined;
  if (roleMap) {
    return `tank-enemy-${faction}-${roleMap[v]}`;
  }
  return enemyTankKeyFor(variant);
}

// ── Mini-boss skin roster (6 cartoon tanks, 2026-06-13) ───────────────────────
//
// miniboss-roster-wire (boss「那個魔王很棒,多產生一些類似的」): a 6-strong roster
// of bespoke cartoon mini-boss tanks (`_boss-roster-extra/extra-<id>.png`, baked
// by `scripts/prep-miniboss-roster.cjs` → `tank-miniboss-<id>.png`). The scene's
// `_spawnMiniBoss` rotates through these so the every-3-waves 小魔王 isn't the
// same purple box each time — pure variety, 0 戰力寫入 (HP/傷害/招式 unchanged).
//
// These are FULL-COLOUR cartoon tanks, so the call site drops the magenta
// MINI_BOSS_TINT when one is shown (tinting a full-colour PNG just muddies it —
// same reasoning as the bespoke boss PNGs). Falls back to the existing
// Graphics-box + tint render when no texture is present (stripped deploy / test).
//
// Direction: every chassis is authored FACING LEFT (barrel/face point left); the
// player is on the LEFT, so the render uses NO flipX (`setFlipX(false)`), same
// contract as the enemy/boss roster.

/** Mini-boss skin ids — must match scripts/prep-miniboss-roster.cjs MINIBOSS_IDS. */
export const MINIBOSS_SPRITE_IDS = [
  'spider', 'hover', 'train', 'drill', 'skull', 'crystal',
] as const;

export type MiniBossSkinId = (typeof MINIBOSS_SPRITE_IDS)[number];

/** Mini-boss skin id → Phaser texture asset key. */
export const MINIBOSS_SPRITE_KEYS: Record<MiniBossSkinId, string> = Object.fromEntries(
  MINIBOSS_SPRITE_IDS.map((id) => [id, `tank-miniboss-${id}`]),
) as Record<MiniBossSkinId, string>;

/** Mini-boss skin id → relative PNG URL. */
export const MINIBOSS_SPRITE_URLS: Record<MiniBossSkinId, string> = Object.fromEntries(
  MINIBOSS_SPRITE_IDS.map((id) => [id, `assets/sprites/tank-miniboss-${id}.png`]),
) as Record<MiniBossSkinId, string>;

/**
 * Deterministically picks which mini-boss skin to show, by a 0-based index
 * (e.g. wave number or a spawn counter). Pure / Phaser-free so the rotation is
 * unit-testable: the same index always yields the same skin, and consecutive
 * indices cycle through all 6 before repeating. Negative/NaN indices clamp to 0.
 */
export function pickMiniBossSkin(index: number): MiniBossSkinId {
  const n = MINIBOSS_SPRITE_IDS.length;
  const i = Number.isFinite(index) ? Math.floor(index) : 0;
  // true modulo (handles negatives) so it always lands in [0, n).
  const idx = ((i % n) + n) % n;
  return MINIBOSS_SPRITE_IDS[idx];
}

/**
 * Returns the mini-boss sprite asset key for a given rotation index. Always a
 * registered key — callers texture-exists gate it and fall back to the Graphics
 * box when the PNG isn't loaded. Phaser-free.
 */
export function miniBossSkinKey(index: number): string {
  return MINIBOSS_SPRITE_KEYS[pickMiniBossSkin(index)];
}

// ── Boss sprite keys (5 AI-gen PNGs) ──────────────────────────────────────────
//
// Wired in by boss-sprite-wire (addresses art-audit finding 6341db5: bosses had
// PNGs on disk but no preload/render wire-up). Files live in
// `public/assets/ai-gen/bosses/*.png` and are preloaded by HorizontalBattleScene.
// When a texture is missing (stripped deployment) the existing Graphics-based
// boss render in _drawBossSprite remains visible.
//
// The values match `ASSETS.boss{ShuMo,FuHun,YuanSu,TianMing,ShiZhong}` in
// `../config.ts` — kept in sync via the BOSS_SPRITE_KEYS table. We don't import
// ASSETS here so this module stays Phaser-free and unit-testable.

/**
 * Boss id → Phaser texture asset key.
 *
 * R-004 hotfix(boss 2026-06-13「魔王的圖壞了」):tier2 四隻(shu-fu/ji-yuan/
 * xing-yu/zi-fu)先前沒登記 → ch2 boss 關的 shu-fu 沒有 PNG overlay,prod 上
 * 變成一塊純色 Graphics 方塊。所有 boss 共用同一張 ansimuz mech(門派色 tint
 * 區分),補登記即修復。守門:boss-sprite-coverage.test.ts 掃三個池逐一比對。
 */
export const BOSS_SPRITE_KEYS: Record<string, string> = {
  'shu-mo':    'boss-shu-mo',
  'fu-hun':    'boss-fu-hun',
  'yuan-su':   'boss-yuan-su',
  'shu-fu':    'boss-shu-fu',
  'ji-yuan':   'boss-ji-yuan',
  'xing-yu':   'boss-xing-yu',
  'zi-fu':     'boss-zi-fu',
  'tian-ming': 'boss-tian-ming',
  'shi-zhong': 'boss-shi-zhong',
  // boss-roster-extra (boss 2026-06-13「boss 寫太少」): 6 隻新「正式魔王」
  // (BOSS_EXTRA_POOL),各有自己的 bespoke p1/p2 PNG (tank-boss-<id>-p1/-p2.png,
  // 由 scripts/prep-boss-roster-extra.cjs 烘焙)。登記後 preload loop 與 phase2
  // 換圖 (bossPhase2SpriteKey) 自動生效,無需改 battle scene。
  'spider':    'boss-spider',
  'hover':     'boss-hover',
  'train':     'boss-train',
  'drill':     'boss-drill',
  'skull':     'boss-skull',
  'crystal':   'boss-crystal',
};

/**
 * Boss id → relative URL of the phase-1 boss PNG.
 *
 * boss-roster-wire (2026-06-13, boss「魔王的圖要多設計 二階段照型要變」): every
 * boss now has its OWN bespoke art (`_boss-roster/<id>-p1.png` baked to
 * `tank-boss-<id>-p1.png` by `scripts/prep-boss-roster.cjs`), replacing the two
 * shared cartoon chassis (demon skull-tank / mech-walker). The phase-2 暴怒
 * 變身 art (裂開露核心/增刺/紅光) lives in BOSS_PHASE2_SPRITE_* below and is
 * swapped in by the scene when the boss enters phase ≥ 2 / berserk.
 *
 * Because each boss is now a full-colour bespoke PNG, the per-faction
 * `setTint(def.color)` overlay is dropped at the call site (它只會把全彩圖弄濁).
 *
 * Direction: every roster boss is drawn FACING LEFT (TANK_ART_SPEC §1, verified
 * 2026-06-13); the player is on the left, so the boss render uses NO flipX.
 */
export const BOSS_SPRITE_URLS: Record<string, string> = Object.fromEntries(
  Object.keys(BOSS_SPRITE_KEYS).map((id) => [
    id,
    `assets/sprites/tank-boss-${id}-p1.png`,
  ]),
);

/**
 * Boss id → Phaser texture asset key for the PHASE-2 暴怒變身 art.
 *
 * Distinct key (`<p1-key>-p2`) so the phase-1 and phase-2 textures coexist in
 * the texture cache; the scene preloads both and `setTexture(p2key)` swaps the
 * boss sprite the moment it enters phase ≥ 2 / berserk. 鐵則:純美術換圖,不改
 * 任何 HP/傷害/數值。
 */
export const BOSS_PHASE2_SPRITE_KEYS: Record<string, string> = Object.fromEntries(
  Object.entries(BOSS_SPRITE_KEYS).map(([id, key]) => [id, `${key}-p2`]),
);

/** Boss id → relative URL of the phase-2 暴怒變身 boss PNG. */
export const BOSS_PHASE2_SPRITE_URLS: Record<string, string> = Object.fromEntries(
  Object.keys(BOSS_PHASE2_SPRITE_KEYS).map((id) => [
    id,
    `assets/sprites/tank-boss-${id}-p2.png`,
  ]),
);

/**
 * Returns the boss sprite asset key for a given boss id, or `undefined` if no
 * PNG is registered for that boss. Callers should treat `undefined` as "stick
 * with the Graphics-based boss render".
 */
export function bossSpriteKey(bossId: string | null | undefined): string | undefined {
  if (!bossId) return undefined;
  return BOSS_SPRITE_KEYS[bossId];
}

/**
 * Returns the phase-2 暴怒變身 boss sprite asset key for a given boss id, or
 * `undefined` when unknown. Used by the scene to `setTexture()` the boss into
 * its變身型 on entering phase ≥ 2 / berserk.
 */
export function bossPhase2SpriteKey(bossId: string | null | undefined): string | undefined {
  if (!bossId) return undefined;
  return BOSS_PHASE2_SPRITE_KEYS[bossId];
}

/**
 * Pure decision fn (boss-roster-wire): should the boss sprite swap to its
 * phase-2 暴怒變身 art this frame?  True iff the boss has crossed into phase ≥ 2
 * (or berserk) AND it hasn't already swapped. Renderer-agnostic / Phaser-free so
 * the swap-trigger logic is unit-testable. The scene owns the `alreadySwapped`
 * flag and only fires the texture swap + transform 演出 once.
 *
 * 鐵則:此函式只回布林「該不該換圖」,絕不碰 HP/傷害/數值。
 */
export function shouldSwapToPhase2(
  state: { phase: number; berserk: boolean; alreadySwapped: boolean },
): boolean {
  if (state.alreadySwapped) return false;
  return state.phase >= 2 || state.berserk === true;
}

// ── 戰鬥端美術 extras (僚機 / 子彈 / 技能特效, 2026-06-13) ─────────────────────
//
// extras-wire (boss「接入新美術——僚機、子彈、主角技能特效」): the newly-generated
// 戰鬥端 art (`_extras-roster/`, baked by `scripts/prep-extras-fx.cjs`) is wired in
// as PURE 演出 — 0 戰力寫入 (CLAUDE.md 鐵則). Every render path is texture-exists
// gated, so a stripped deployment / unit-test harness falls back to the existing
// Phaser.Graphics shapes (bullets keep their glowing-bolt Graphics; the companion
// keeps the old `companion-drone` PNG / Graphics).
//
// File naming mirrors the tank tables: key == frame/asset key, URL under
// `assets/sprites/`. All assets live as standalone PNGs (NOT in the tanks atlas):
// the source art is 200–500px and varied-size, so packing it into the tidy 64×64
// tank atlas would bloat it — standalone `load.image` matches the existing
// companion-drone / cover / boss precedent.

/** Player main-cannon bullet sprite key, per 門派 (青/紫/紅/綠/金). */
export const BULLET_SPRITE_KEYS: Record<FactionId, string> = {
  celestial: 'bullet-celestial',
  runic:     'bullet-runic',
  techno:    'bullet-techno',
  elemental: 'bullet-elemental',
  dominion:  'bullet-dominion',
};

/** 門派 → relative URL of the tinted bullet PNG. */
export const BULLET_SPRITE_URLS: Record<FactionId, string> = Object.fromEntries(
  (Object.entries(BULLET_SPRITE_KEYS) as [FactionId, string][]).map(
    ([f, key]) => [f, `assets/sprites/${key}.png`],
  ),
) as Record<FactionId, string>;

/**
 * Returns the bullet sprite asset key for a faction. Always non-empty —
 * defaults to celestial for unknown/missing factions so the texture-exists gate
 * has a key to look up (and falls back to Graphics when that texture is absent).
 */
export function bulletKeyFor(faction: FactionId | string | undefined | null): string {
  if (!faction) return BULLET_SPRITE_KEYS.celestial;
  return (BULLET_SPRITE_KEYS as Record<string, string>)[faction]
    ?? BULLET_SPRITE_KEYS.celestial;
}

/** Companion 僚機 (圓眼支援機) sprite key + URL. */
export const DRONE_SPRITE_KEY = 'tank-drone-orb';
export const DRONE_SPRITE_URL = `assets/sprites/${DRONE_SPRITE_KEY}.png`;

/**
 * Skill-FX sprite keys, keyed by a short effect name. Wired to the slot-0 主角
 * 技能施放 per 門派 in HorizontalBattleScene._fireHeroSlot0:
 *   meteor  → dominion 範圍轟炸 (隕石)
 *   shield  → runic 符文盾
 *   frost   → elemental 火柱 / 冰爆場
 *   spread  → celestial 爆發連射 (散彈)
 *   thunder → techno 偵察提速 / 泛用大絕 (電系)
 */
export const SKILL_FX_SPRITE_KEYS = {
  meteor:  'skill-fx-meteor',
  shield:  'skill-fx-shield',
  frost:   'skill-fx-frost',
  spread:  'skill-fx-spread',
  thunder: 'skill-fx-thunder',
} as const;

export type SkillFxName = keyof typeof SKILL_FX_SPRITE_KEYS;

/** skill-fx name → relative PNG URL. */
export const SKILL_FX_SPRITE_URLS: Record<string, string> = Object.fromEntries(
  Object.values(SKILL_FX_SPRITE_KEYS).map((key) => [key, `assets/sprites/${key}.png`]),
);

/** 門派 → which skill-fx its slot-0 技能 plays. Pure (no Phaser dep). */
export const FACTION_SKILL_FX: Record<FactionId, SkillFxName> = {
  celestial: 'spread',
  runic:     'shield',
  techno:    'thunder',
  elemental: 'frost',
  dominion:  'meteor',
};

/**
 * Returns the skill-fx sprite key for a 門派's slot-0 技能, or `undefined` for
 * an unknown faction (caller skips the FX overlay — pure 演出, never blocks the
 * gameplay effect). Phaser-free / unit-testable.
 */
export function skillFxKeyForFaction(faction: FactionId | string | undefined | null): string | undefined {
  if (!faction) return undefined;
  const name = FACTION_SKILL_FX[faction as FactionId];
  return name ? SKILL_FX_SPRITE_KEYS[name] : undefined;
}

/**
 * Choose which player faction's tank to display. Picks the faction with the
 * highest accumulated XP; ties are broken by the canonical order (celestial
 * first). When all factionXP entries are zero (new player), returns 'celestial'.
 */
export function pickDisplayFaction(
  factionXP: Record<FactionId, number> | null | undefined,
): FactionId {
  if (!factionXP) return 'celestial';
  const order: FactionId[] = ['celestial', 'runic', 'techno', 'elemental', 'dominion'];
  let best: FactionId = 'celestial';
  let bestXP = -1;
  for (const f of order) {
    const xp = factionXP[f] ?? 0;
    if (xp > bestXP) {
      best = f;
      bestXP = xp;
    }
  }
  return best;
}
